/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../AnalyticsBaseWidget',
	'../../DynamicFileLoader',
	'./FilterWidgetCommonView',
	'../../filters/controls/FilterErrorView',
	'../../filters/finder/FilterModuleFinder',
	'../../filters/FilterMetadataHelper',
	'../livewidget/nls/StringResources',
	'../../lib/@waca/dashboard-common/dist/api/Error'
], function (BaseWidget, DynamicFileLoader, FilterWidgetCommonView, FilterErrorView, FilterModuleFinder, FilterMetadataHelper, resources,APIError) {
	'use strict';

	/*
	* A filter widget is composed from a view common across all filter widget types and
	* a view specific to a widget type. For example a 'member' filter widget is composed
	* of:
	*	(1) The common views
	*	(2) The member filter widget view
	*
	*/
	var FilterWidget = BaseWidget.extend({

		shouldRefreshOnShow: true,

		init: function (params) {
			FilterWidget.inherited('init', this, arguments);
			this.item = params.initialConfigJSON.item;
			var commonViewConfig = { $parentEl : this.$el };
			this.commonView = new FilterWidgetCommonView(commonViewConfig);
			this.extraOptions = { commonView: this.commonView };
			this.params = params;
			this.dashboard = params.dashboardApi;
			this.registerSourceUsage();
		},

		destroy: function() {
			if ( this.filterInstance ) {
				this.filterInstance.remove();
			}
			this.extraOptions  = null;
			if (this.commonView) {
				this.commonView.remove();
				this.commonView = null;
			}
			this.unregisterSourceUsage();
			FilterWidget.inherited('destroy', this, arguments);
		},

		registerSourceUsage: function() {
			const dataSourceSvc = this.dashboard.getFeature('dataSources.deprecated');
			this.sourceCollection = dataSourceSvc.getSourcesCollection();
			this.sourceCollection.registerUsage(this.id, this.params.initialConfigJSON.properties.sourceId);
		},

		unregisterSourceUsage: function() {
			if (this.sourceCollection) {
				this.sourceCollection.deregisterUsage(this.id);
			}
		},

		/**
		 * Handler for when the widget chrome is selected.
		 */
		onChromeSelected: function() {
			FilterWidget.inherited('onChromeSelected', this, arguments);
		},

		/**
		* Return Async dep
		* [FIlterControllerModule, AjaxService]
		*/
		loadFilterControlDep: function(dataSources){

			var sourceId = this.params.initialConfigJSON.properties.sourceId;
			var itemId = this.params.initialConfigJSON.properties.itemId;

			var pageContextPromise = Promise.resolve(this.dashboard.getFeature('PageContextService').getPageContext());

			var filterModulePromise = Promise.try(() => {
				var options = {
					origin: 'filterWidget',
					sourceId: sourceId,
					itemId: itemId,
					dataSources: dataSources
				};
				return FilterModuleFinder.getModule(options)
					.then(modulePath => DynamicFileLoader.load([modulePath]))
					.then(modules => modules[0]);
			});

			var metadataPromise =  dataSources.getModule(sourceId)
				.then(function(module) {
					return module.getMetadataColumn(itemId);
				});

			return Promise.all([filterModulePromise, pageContextPromise, metadataPromise]);
		},

		onFilterDepLoadComplete: function(FilterDeps) {
			var params = this.params || {};
			var FilterModule = FilterDeps[0];
			var PageContext = FilterDeps[1];
			var metadataColumn = FilterDeps[2];

			// overwrite with the hard filter scope if needed
			if (this.hardFilterScope) {
				params.containerPageId = this.hardFilterScope;
			}

			var itemContext = params.initialConfigJSON.properties;
			var options = {
				metadataColumn: metadataColumn,
				dashboardApi: this.dashboard,
				pageContextAPI: PageContext,
				containerPageId: params.initialConfigJSON.properties.scope,
				isCompact: true,
				initialConfigJSON: {
					properties: itemContext
				},
				ajaxSvc: this.dashboard.getGlassCoreSvc('.Ajax'),
				hasSearchIcon: true
			};

			this.filterInstance = new FilterModule(options, { commonView: this.commonView });

			if(!this._isVisible()) {
				this.shouldRefreshOnShow = true;
			}

			const dataSources = this.dashboard.getFeature('dataSources.deprecated');
			return this._updatePageContextMetadate(itemContext, PageContext, dataSources)
				.then(this.filterInstance.initialize.bind(this.filterInstance))
				.then(this.filterInstance.render.bind(this.filterInstance))
				.then(this.applyCommonProperties.bind(this));
		},

		_updatePageContextMetadate: function (itemContext, pageContextAPI, dataSources) {
			return FilterMetadataHelper.getColumnMetadataInfo(dataSources,itemContext.sourceId, itemContext.itemId)
				.then(metadataInfo => {
					var pageContextItem = pageContextAPI.getPageContextItem(itemContext);
					if (pageContextItem) {
						pageContextItem.updateMetadata(metadataInfo);
					}
				});
		},

		onContainerReady: function (/*containContext*/) {
			FilterWidget.inherited('onContainerReady', this, arguments);
			this.params.model = this.model;
			this.addWhiteListAttrs('isFilterActive');

			const dataSources = this.dashboard.getFeature('dataSources.deprecated');
			return this.dashboard.getCanvasWhenReady()
				.then(() => {
					return this.loadFilterControlDep(dataSources)
						.then(this.onFilterDepLoadComplete.bind(this));
				});
		},

		/**
		 * Returns if the DOM element is visible or not
		 *
		 * @param {boolean} true if the DOM element is visible else return false
		 */
		_isVisible: function() {
			return this.$el.is(':visible');
		},

		/**
		 * Displays the filter error view
		 * @param {object} [error] the error that's been thrown (optional)
		 * @returns {object} promise resolving with the rendering of the error view
		 */
		_onError: function(error) {
			let errorView = new FilterErrorView({
				commonView: this.commonView,
				isCompact: this.params.isCompact || false,
				dashboardApi: this.dashboard
			});

			const state = this.content.getFeature('state.internal');
			state.setError(new APIError({ msg: error.message, params: { errorInfo: error ? error.errorInfo : undefined } }));

			return errorView.renderViewComposition(error);
		},

		resize: function() {
			if(!this._isVisible()) {
				this.shouldRefreshOnShow = true;
			}
		},

		onShow: function () {
			FilterWidget.inherited('onShow', this, arguments);
			if (this.shouldRefreshOnShow && this.filterInstance && this.filterInstance.onShow) {
				this.shouldRefreshOnShow = false;
				this.filterInstance.ready.then(this.filterInstance.onShow.bind(this.filterInstance));
			}
		}
	});

	return FilterWidget;
});
